// 🔐 accounts (09-30): the app-level sign-in sheet + account toast. Mounted
// ONCE in App.js beside BugReportModal (same embed/theater exclusions).
// Renders NOTHING while sign-in is off; when it is on, only an empty live
// region until openSignIn() / showAccountToast() fires.
// The sheet opens for a signed-out visitor and stays up until its card is done
// (step 2, "Found on this browser", runs after the person is signed in).
import React, { useEffect, useRef, useState } from 'react';
import { ACCOUNT_CSS } from './accountStyles';
import { SIGNIN_EVENT, ACCOUNT_TOAST_EVENT } from './accountEvents';
import { useAccount } from './useAccount';
import SignInCard, { savedLine } from './SignInCard';
import { prefersDark } from '../utils/themeChoice';

const TOAST_MS = 4500;
const FOCUSABLE = 'button:not([disabled]), a[href], input:not([disabled]), iframe, [tabindex]:not([tabindex="-1"])';

export default function AccountLayer() {
  const account = useAccount();
  const [open, setOpen] = useState(false);
  const [toast, setToast] = useState(null);
  const [dark, setDark] = useState(true);
  const statusRef = useRef(account.status);
  statusRef.current = account.status;
  const boxRef = useRef(null);
  const returnRef = useRef(null);

  useEffect(() => {
    const onOpen = () => {
      if (statusRef.current !== 'signed_out' && statusRef.current !== 'loading') return;
      returnRef.current = document.activeElement;
      setDark(prefersDark());
      setOpen(true);
    };
    const onToast = (e) => { setDark(prefersDark()); setToast({ text: e.detail && e.detail.text, at: Date.now() }); };
    window.addEventListener(SIGNIN_EVENT, onOpen);
    window.addEventListener(ACCOUNT_TOAST_EVENT, onToast);
    return () => { window.removeEventListener(SIGNIN_EVENT, onOpen); window.removeEventListener(ACCOUNT_TOAST_EVENT, onToast); };
  }, []);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), TOAST_MS);
    return () => clearTimeout(t);
  }, [toast]);

  // Focus moves into the sheet when it opens, Tab stays inside it, Escape closes it,
  // and focus goes back to the door that opened it.
  useEffect(() => {
    if (!open) return undefined;
    if (boxRef.current) boxRef.current.focus();
    const onKey = (e) => {
      if (e.key === 'Escape') { setOpen(false); return; }
      if (e.key !== 'Tab' || !boxRef.current) return;
      const els = [...boxRef.current.querySelectorAll(FOCUSABLE)];
      if (!els.length) return;
      const first = els[0];
      const last = els[els.length - 1];
      if (e.shiftKey && (document.activeElement === first || document.activeElement === boxRef.current)) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      const el = returnRef.current;
      if (el && typeof el.focus === 'function' && document.contains(el)) el.focus();
    };
  }, [open]);

  if (account.status === 'off' && !open && !toast) return null;

  return (
    <div className="ms-acct" data-mode={dark ? 'dark' : 'light'}>
      <style>{ACCOUNT_CSS}</style>
      {open && (
        <div className="ms-acct-scrim" onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}>
          <div role="dialog" aria-modal="true" aria-labelledby="ms-signin-title" tabIndex={-1} ref={boxRef} style={{ outline: 'none' }}>
            <SignInCard
              clientId={account.googleClientId}
              dark={dark}
              onClose={() => setOpen(false)}
              onDone={(r) => { setOpen(false); setToast({ text: savedLine(r), at: Date.now() }); }}
            />
          </div>
        </div>
      )}
      {/* Mounted before any text arrives, so screen readers announce the toast. */}
      <div role="status" aria-live="polite">
        {toast && toast.text && <div className="ms-acct-toast"><i aria-hidden="true" />{toast.text}</div>}
      </div>
    </div>
  );
}
